<template>
  <div class="mb-6">
    <div class="flex flex-row items-center mb-1 ml-2">
      <div class="text-lg font-poppins tracking-wide text-dark-primary">
        Responsiveness
      </div>
      <div class="ml-3 text-xs text-gray-500">
        ELB latency + alarm state from CloudWatch.
      </div>
      <div v-if="errorMsg" class="ml-3 text-xs text-rose-600">
        {{ errorMsg }}
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1">
      <MetricCard
        title="ELB Latency p50 (1h)"
        :value="loading ? '—' : (data.latency_p50_1h_ms != null ? `${data.latency_p50_1h_ms} ms` : '—')"
        subtitle="Median, last hour"
        to="/admin-dashboard/responsiveness"
      />
      <MetricCard
        title="ELB Latency p95 (1h)"
        :value="loading ? '—' : (data.latency_p95_1h_ms != null ? `${data.latency_p95_1h_ms} ms` : '—')"
        subtitle="95th percentile"
        :valueTone="latencyTone(data.latency_p95_1h_ms, 1500)"
        to="/admin-dashboard/responsiveness"
      />
      <MetricCard
        title="ELB Latency p99 (1h)"
        :value="loading ? '—' : (data.latency_p99_1h_ms != null ? `${data.latency_p99_1h_ms} ms` : '—')"
        subtitle="99th percentile"
        :valueTone="latencyTone(data.latency_p99_1h_ms, 3000)"
        to="/admin-dashboard/responsiveness"
      />
      <MetricCard
        title="5xx Rate (1h)"
        :value="loading ? '—' : (data.error_5xx_rate_1h_pct != null ? `${data.error_5xx_rate_1h_pct.toFixed(2)}%` : '—')"
        subtitle="ELB 5xx / total requests"
        :valueTone="data.error_5xx_rate_1h_pct > 0.5 ? 'bad' : 'default'"
        to="/admin-dashboard/responsiveness"
      />
      <MetricCard
        title="Alarm Status"
        :value="loading ? '—' : (data.alarm_state || 'UNKNOWN')"
        :subtitle="data.alarm_state_changed_at ? `Since ${formatTimestamp(data.alarm_state_changed_at)}` : 'ELB High Latency Alarm'"
        :valueTone="alarmTone(data.alarm_state)"
        :borderTone="alarmTone(data.alarm_state)"
        to="/admin-dashboard/responsiveness"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import MetricCard from './MetricCard.vue'
import { getAdminDashboardResponsiveness } from '@/services/Company'

const store = useStore()
const loading = ref(true)
const errorMsg = ref('')
const data = ref({})

function latencyTone(ms, warnThreshold) {
  if (ms == null) return 'default'
  if (ms >= warnThreshold * 2) return 'bad'
  if (ms >= warnThreshold) return 'warn'
  return 'good'
}

function alarmTone(state) {
  if (state === 'OK') return 'good'
  if (state === 'ALARM') return 'bad'
  if (state === 'INSUFFICIENT_DATA') return 'warn'
  return 'default'
}

function formatTimestamp(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

async function load() {
  const token = store.state.auth.token
  if (!token) {
    errorMsg.value = 'Not authenticated.'
    loading.value = false
    return
  }
  try {
    data.value = await getAdminDashboardResponsiveness(token)
    if (data.value.cloudwatch_error) errorMsg.value = `cw: ${data.value.cloudwatch_error}`
  } catch (e) {
    const status = e && e.response && e.response.status
    errorMsg.value = status ? `HTTP ${status}` : (e && e.message) || 'unknown error'
  } finally {
    loading.value = false
  }
}

load()
</script>
